/**
 * Feature hook: OneDrive list, filtered, delete, download.
 * Preview in Teams uses usePreviewInTeams + previewInTeamsSlice (single API). No preview URL here.
 * downloadOneDriveToLocal: fetches download URL, then opens it so the file saves to the user's machine.
 */
import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  fetchOneDriveDocuments,
  deleteOneDriveDocument,
  deleteOneDriveDocumentsBulk,
  fetchOneDriveDownloadUrl,
  setDownloadDialogOpen,
} from "../slices/oneDrive";
import { onDelOneDriveDocument as delOneDriveDocumentAction } from "../slices/documentsAction";
import { logger } from "../../utils/logger";

export function useOneDrive() {
  const dispatch = useAppDispatch();
  const listState = useAppSelector((state) => state.OneDriveDocuments);
  const filteredOneDriveDocuments = useAppSelector((state) => state.FilteredOneDriveDocuments);
  const downloadState = useAppSelector((state) => state.OneDriveDownload);
  const downloadUrl = downloadState?.downloadUrl ?? "";

  const oneDriveDocuments = useMemo(
    () => ({
      ...listState,
      oneDriveDocumentDownloadUrl: downloadUrl,
    }),
    [listState, downloadUrl]
  );

  const fetchOneDriveDocumentsAction = useCallback(
    (teamId, channelId, tenantId, pageSize, searchText, documentType, fileRoot, isOnedrive) =>
      dispatch(fetchOneDriveDocuments({ teamId, channelId, tenantId, pageSize, searchText, documentType, fileRoot, isOnedrive })),
    [dispatch]
  );

  const deleteOneDriveDocumentAction = useCallback(
    (membershipType, fileId, teamId, document, operationType, oneDriveRoot, channelId) =>
      dispatch(deleteOneDriveDocument({ membershipType, fileId, teamId, document, operationType, oneDriveRoot, channelId })),
    [dispatch]
  );

  const deleteOneDriveDocumentsBulkAction = useCallback(
    (membershipType, teamId, channelId, tenantId, oneDriveRoot, documentIds) =>
      dispatch(
        deleteOneDriveDocumentsBulk({
          membershipType,
          teamId,
          channelId,
          tenantId,
          oneDriveRoot,
          documentIds,
        }),
      ),
    [dispatch],
  );

  const delOneDriveDocument = useCallback(
    (fileId, teamId) => dispatch(delOneDriveDocumentAction(fileId, teamId)),
    [dispatch]
  );

  const fetchOneDriveDownloadUrlAction = useCallback(
    (teamId, channelId, tenantId, documentId, fileRoot, operationType, options = {}) =>
      dispatch(
        fetchOneDriveDownloadUrl({
          teamId,
          channelId,
          tenantId,
          documentId,
          fileRoot,
          operationType,
          ...options,
        }),
      ),
    [dispatch]
  );

  const downloadOneDriveToLocal = useCallback(
    async (teamId, channelId, tenantId, documentId, fileRoot, operationType, isLocal = false) => {
      if (!documentId) {
        logger.warn("[useOneDrive] downloadOneDriveToLocal skipped: no documentId", null, "DIALOG");
        return;
      }
      // Only when isLocal: open blank window in same user gesture so the browser doesn't block it
      const downloadWindow = isLocal ? window.open("", "_self", "noopener,noreferrer") : null;
      if (downloadWindow) downloadWindow.opener = null;

      const result = await dispatch(
        fetchOneDriveDownloadUrl({
          teamId,
          channelId,
          tenantId,
          documentId,
          fileRoot,
          operationType
        })
      );
      if (isLocal && result.meta?.requestStatus === "fulfilled" && result.payload && downloadWindow && !downloadWindow.closed) {
        downloadWindow.location.href = result.payload;
      } else if (downloadWindow && !downloadWindow.closed && result.meta?.requestStatus !== "fulfilled") {
        downloadWindow.close();
      }
    },
    [dispatch]
  );

  const closeDownloadDialog = useCallback(() => {
    dispatch(setDownloadDialogOpen(false));
  }, [dispatch]);

  return {
    oneDriveDocuments,
    filteredOneDriveDocuments,
    fetchOneDriveDocuments: fetchOneDriveDocumentsAction,
    deleteOneDriveDocument: deleteOneDriveDocumentAction,
    deleteOneDriveDocumentsBulk: deleteOneDriveDocumentsBulkAction,
    delOneDriveDocument,
    fetchOneDriveDownloadUrl: fetchOneDriveDownloadUrlAction,
    downloadOneDriveToLocal,
    closeDownloadDialog,
    downloadState: {
      isOpen: downloadState?.isOpen ?? false,
      loading: downloadState?.loading ?? false,
      isError: downloadState?.isError ?? false,
      message: downloadState?.message ?? "",
    },
  };
}
